package gr.tech.GRAttestation.components.inputs

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.styles.*
import gr.tech.GRAttestation.util.format
import gr.tech.GRAttestation.util.formatCurrency
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.events.SyntheticChangeEvent
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement

@Composable
fun GRStringInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: String,
    hasError: Boolean = false,
    labelError: String = " ",
    minLength: Int = 3,
    maxLength: Int = 128,
    isRequired: Boolean = false,
    title: String = "",
    onInputChange: (SyntheticChangeEvent<String, HTMLInputElement>) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) { Text(labelText) }
        Input(InputType.Text,
            attrs = GeneralInputStyle.toModifier().then(inputModifier)
                .id(inputId)
                .thenIf(hasError, GeneralInputErrorStyle.toModifier())
                .toAttrs {
                    if (isRequired) required()
                    minLength(minLength)
                    maxLength(maxLength)
                    attr("value", value)
                    title(title)
                    onChange { onInputChange(it) }
                }
        )
        P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
            Text(labelError)
        }
    }
}

@Composable
fun GRDollarInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: Long,
    hasError: Boolean = false,
    labelError: String = " ",
    placeholder: Long = 0,
    isRequired: Boolean = false,
    title: String = "",
    onInputChange: (SyntheticChangeEvent<String, HTMLInputElement>) -> Unit,
) {
    val inputValue = (value / 100.0).format(2)

    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) { Text(labelText) }

        Input(
            type = InputType.Text,
            attrs = GeneralInputStyle.toModifier()
                .then(inputModifier)
                .id(inputId)
                .thenIf(hasError, GeneralInputErrorStyle.toModifier())
                .toAttrs {
                    if (isRequired) required()
                    attr("value", inputValue)
                    placeholder(formatCurrency(placeholder))
                    title(title)
                    onChange { onInputChange(it) }
                }
        )

        P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
            Text(labelError)
        }
    }
}


@Composable
fun GRIntInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: String,
    hasError: Boolean = false,
    labelError: String = " ",
    minLength: Int = 3,
    maxLength: Int = 128,
    isRequired: Boolean = false,
    title: String = "",
    onInputChange: (SyntheticChangeEvent<Number?, HTMLInputElement>) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) { Text(labelText) }
        Input(InputType.Number,
            attrs = GeneralInputStyle.toModifier().then(inputModifier)
                .id(inputId)
                .thenIf(hasError, GeneralInputErrorStyle.toModifier())
                .toAttrs {
                    if (isRequired) required()
                    minLength(minLength)
                    maxLength(maxLength)
                    attr("value", value)
                    title(title)
                    onChange { onInputChange(it) }
                }
        )
        P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
            Text(labelError)
        }
    }
}

@Composable
fun GRDateInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: String,
    hasError: Boolean = false,
    labelError: String = " ",
    minLength: Int = 3,
    maxLength: Int = 128,
    title: String = "",
    onInputChange: (SyntheticChangeEvent<String, HTMLInputElement>) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) { Text(labelText) }
        Input(InputType.Date,
            attrs = GeneralInputStyle.toModifier().then(inputModifier)
                .id(inputId)
                .thenIf(hasError, GeneralInputErrorStyle.toModifier())
                .toAttrs {
                    style {
                        property("appearance", "none")
                    }
                    attr("value", value)
                    title(title)
                    onChange { onInputChange(it) }
                }
        )
        P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
            Text(labelError)
        }
    }
}

@Composable
fun GRSelectInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    selectModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: String,
    options: List<Pair<String, String>>, // Pair<Index String, Label String>
    hasError: Boolean = false,
    labelError: String = " ",
    isRequired: Boolean = false,
    title: String = "",
    onInputChange: (SyntheticChangeEvent<String?, HTMLSelectElement>) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) {
            Text(labelText)
        }

        Select(
            attrs = GeneralInputStyle.toModifier()
                .then(selectModifier)
                .id(inputId)
                .thenIf(hasError, GeneralInputErrorStyle.toModifier())
                .toAttrs {
                    if (isRequired) required()
                    title(title)
                    attr("value", value)
                    onChange { onInputChange(it) }
                }
        ) {
            // Placeholder option
            Option(
                value = "",
                attrs = Modifier.toAttrs {
                    attr("disabled", "")
                    attr("hidden", "")
                    if (value.isEmpty()) attr("selected", "")
                }
            ) {
                Text("Select Option")
            }

            // Actual options
            options.forEach { (optionValue, optionLabel) ->
                Option(
                    value = optionValue,
                    attrs = Modifier.toAttrs {
                        if (optionValue == value) attr("selected", "")
                    }
                ) {
                    Text(optionLabel)
                }
            }
        }

        P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
            Text(labelError)
        }
    }
}
